import { Link } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge, type BadgeTone } from "@/components/ui/badge"
import { ComingSoonButton } from "@/components/ui/coming-soon-button"

// Settings → Apps. Phase 1 publisher index. Lists every App row owned
// by the current company (`current_company.published_apps`) and exposes
// a single primary CTA — Create App — gated to Valet system admins
// during the closed beta. Every other operator sees a `<ComingSoonButton>`
// rendered in the same spot so they know the feature exists and where
// it lives, but can't fire the create flow.

type AppStatus = "draft" | "private" | "public" | "archived"

type Placement = { context: string; location: string }

type AppRow = {
  id: number
  key: string
  name: string
  description: string | null
  icon: string | null
  status: AppStatus
  manifestVersion: number
  defaultMinRole: "agent" | "agent_manager" | "admin"
  supportedPlacements: Placement[]
  installCount: number
  publisherCompany: { id: number; name: string }
}

type Props = {
  apps: AppRow[]
  isAdmin: boolean
}

const STATUS_TONE: Record<AppStatus, BadgeTone> = {
  draft:    "warning",
  private:  "neutral",
  public:   "success",
  archived: "neutral",
}

const STATUS_LABEL: Record<AppStatus, string> = {
  draft:    "Draft",
  private:  "Private",
  public:   "Public",
  archived: "Archived",
}

const COMING_SOON_REASON = "Invite-only during the beta — contact your Valet rep"

export default function SettingsAppsIndex({ apps, isAdmin }: Props) {
  return (
    <AppShell>
      <PageHeader
        eyebrow="Settings"
        title="Apps"
        subtitle="Custom UI extensions you publish into the Valet platform."
        actions={isAdmin ? (
          <Link href="/settings/apps/new">
            <Button>
              <Plus size={14} strokeWidth={2.5} className="mr-1" /> Create App
            </Button>
          </Link>
        ) : (
          <ComingSoonButton reason={COMING_SOON_REASON}>
            <Plus size={14} strokeWidth={2.5} className="mr-1" /> Create App
          </ComingSoonButton>
        )}
      />

      {apps.length === 0 ? (
        <EmptyState isAdmin={isAdmin} />
      ) : (
        <>
          {/* Desktop: full table */}
          <Card className="hidden overflow-hidden p-0 lg:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-white/[0.03] text-xs uppercase tracking-wide text-app-subtle">
                  <th className="px-5 py-3 text-left font-medium">Name</th>
                  <th className="px-4 py-3 text-left font-medium">Key</th>
                  <th className="px-4 py-3 text-left font-medium">Publisher</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">Placements</th>
                  <th className="px-4 py-3 text-left font-medium">Manifest</th>
                  <th className="px-4 py-3 text-left font-medium">Installs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {apps.map((a) => (
                  <AppRow key={a.id} app={a} />
                ))}
              </tbody>
            </table>
          </Card>

          {/* Mobile/tablet: card list */}
          <div className="space-y-3 lg:hidden">
            {apps.map((a) => (
              <AppCard key={a.id} app={a} />
            ))}
          </div>
        </>
      )}
    </AppShell>
  )
}

function AppCard({ app }: { app: AppRow }) {
  return (
    <Link
      href={`/settings/apps/${app.id}`}
      className="block rounded-card border border-border bg-app-panel p-4 transition hover:border-brand-red/40 hover:bg-white/[0.04]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-app-text">{app.name}</div>
          {app.description && (
            <div className="mt-0.5 line-clamp-2 text-xs text-app-muted">{app.description}</div>
          )}
        </div>
        <Badge tone={STATUS_TONE[app.status]}>{STATUS_LABEL[app.status]}</Badge>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
        <div className="min-w-0">
          <dt className="text-app-subtle">Key</dt>
          <dd>
            <code className="break-all rounded bg-white/5 px-1.5 py-0.5 font-mono text-[11px] text-app-text">
              {app.key}
            </code>
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-app-subtle">Publisher</dt>
          <dd className="truncate text-app-muted">{app.publisherCompany.name}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-app-subtle">Manifest</dt>
          <dd className="font-mono text-app-muted">v{app.manifestVersion}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-app-subtle">Installs</dt>
          <dd className="text-app-text">{app.installCount}</dd>
        </div>
      </dl>
      {app.supportedPlacements.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {app.supportedPlacements.map((p, idx) => (
            <span
              key={`${p.context}.${p.location}.${idx}`}
              className="inline-flex items-center rounded-chip border border-border bg-white/5 px-2 py-0.5 font-mono text-[10px] text-app-muted"
            >
              {p.context}.{p.location}
            </span>
          ))}
        </div>
      )}
    </Link>
  )
}

function AppRow({ app }: { app: AppRow }) {
  return (
    <tr
      className="cursor-pointer transition hover:bg-white/[0.04]"
      onClick={() => {
        // Navigate via plain location — the row is decorative and Inertia
        // doesn't surface a row-level Link primitive. Anchor on Name keeps
        // keyboard a11y intact through the focused link.
        window.location.assign(`/settings/apps/${app.id}`)
      }}
    >
      <td className="px-5 py-3">
        <Link
          href={`/settings/apps/${app.id}`}
          onClick={(e) => e.stopPropagation()}
          className="font-medium text-app-text hover:text-brand-red"
        >
          {app.name}
        </Link>
        {app.description && (
          <div className="mt-0.5 truncate text-xs text-app-muted">{app.description}</div>
        )}
      </td>
      <td className="px-4 py-3">
        <code className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-xs text-app-text">
          {app.key}
        </code>
      </td>
      <td className="px-4 py-3 text-app-muted">{app.publisherCompany.name}</td>
      <td className="px-4 py-3">
        <Badge tone={STATUS_TONE[app.status]}>{STATUS_LABEL[app.status]}</Badge>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1.5">
          {app.supportedPlacements.length === 0 ? (
            <span className="text-xs text-app-subtle">—</span>
          ) : (
            app.supportedPlacements.map((p, idx) => (
              <span
                key={`${p.context}.${p.location}.${idx}`}
                className="inline-flex items-center rounded-chip border border-border bg-white/5 px-2 py-0.5 font-mono text-[10px] text-app-muted"
              >
                {p.context}.{p.location}
              </span>
            ))
          )}
        </div>
      </td>
      <td className="px-4 py-3 font-mono text-xs text-app-muted">v{app.manifestVersion}</td>
      <td className="px-4 py-3 text-app-text">{app.installCount}</td>
    </tr>
  )
}

function EmptyState({ isAdmin }: { isAdmin: boolean }) {
  return (
    <Card className="p-10 text-center">
      <div className="mx-auto max-w-md space-y-3">
        <h2 className="text-lg font-semibold">No published Apps yet</h2>
        <p className="text-sm text-app-muted">
          A Valet App is a custom UI extension that your tenants can mount
          into a ticket sidebar. Build one in Phase 1 to render order
          history, account state, or any tenant-specific context the
          operator needs.
        </p>
        <div className="pt-3">
          {isAdmin ? (
            <Link href="/settings/apps/new">
              <Button>
                <Plus size={14} strokeWidth={2.5} className="mr-1" /> Create your first App
              </Button>
            </Link>
          ) : (
            <ComingSoonButton reason={COMING_SOON_REASON}>
              <Plus size={14} strokeWidth={2.5} className="mr-1" /> Create your first App
            </ComingSoonButton>
          )}
        </div>
      </div>
    </Card>
  )
}
